import { makeColorToken } from "./token.color";
import type {
  ColorTokenOutput,
  ColorToken,
  ColorTokenInput,
} from "./token.color";
import type { OverridenTokenCSSProperties } from "./util.make-generic-token";
import { makeGenericToken } from "./util.make-generic-token";

export type InteractiveColorTokenInput = {
  default: ColorToken | ColorTokenInput;
  hover: ColorToken | ColorTokenInput;
  active: ColorToken | ColorTokenInput;
};

export type InteractiveColorToken = {
  (options?: {
    state?: keyof InteractiveColorTokenInput;
    alpha?: number;
  }): ColorTokenOutput;

  __override: (
    input:
      | Partial<InteractiveColorTokenInput>
      | InteractiveColorToken
      | undefined,
  ) => OverridenTokenCSSProperties;

  __extend(propName: `--${string}`): InteractiveColorToken;

  default: ColorToken;
  hover: ColorToken;
  active: ColorToken;
};

export function makeInteractiveColorToken(
  propName: `--${string}`,
  input: InteractiveColorTokenInput | InteractiveColorToken,
): InteractiveColorToken {
  const tokens = {
    default:
      typeof input.default === "string"
        ? makeColorToken(`${propName}--default`, input.default)
        : input.default.__extend(`${propName}--default`),

    hover:
      typeof input.hover === "string"
        ? makeColorToken(`${propName}--hover`, input.hover)
        : input.hover.__extend(`${propName}--hover`),

    active:
      typeof input.active === "string"
        ? makeColorToken(`${propName}--active`, input.active)
        : input.active.__extend(`${propName}--active`),
  };

  return makeGenericToken(
    function format(options?: {
      state?: keyof InteractiveColorTokenInput;
      alpha?: number;
    }) {
      switch (options?.state) {
        case "hover":
          return tokens.hover(options);

        case "active":
          return tokens.active(options);

        default:
          return tokens.default(options);
      }
    },

    function override(
      nextValue:
        | Partial<InteractiveColorTokenInput>
        | InteractiveColorToken
        | undefined,
    ) {
      if (nextValue === undefined) {
        return {};
      }

      return {
        ...tokens.default.__override(nextValue.default),
        ...tokens.hover.__override(nextValue.hover),
        ...tokens.active.__override(nextValue.active),
      };
    },

    function extend(propName, token) {
      return makeInteractiveColorToken(propName, token);
    },

    {
      default: tokens.default,
      hover: tokens.hover,
      active: tokens.active,
    },
  );
}
